import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Alert, Box, Button, Stack, TextField } from '@mui/material';
import { DataGrid, GridPaginationModel } from '@mui/x-data-grid';

import SearchService, { GetPlaceDetailsResponse } from '~/services/SearchService';

type DeprioritisedPolygon =
  | GetPlaceDetailsResponse['result']
  | {
      placeId: string;
      name: string;
      type: string;
    };

const DEFAULT_PAGINATION_MODEL: GridPaginationModel = {
  page: 0,
  pageSize: 100,
};

async function getDeprioritisedPolygon(placeId: string): Promise<DeprioritisedPolygon> {
  try {
    const response = await SearchService.getPlaceDetails(placeId);
    return response.result;
  } catch (error) {
    return {
      placeId,
      name: placeId,
      type: 'unknown',
    };
  }
}

export default function DeprioritisedPolygonsPage() {
  const [deprioritisedPlaceIds, setDeprioritisedPlaceIds] = useState<Array<string>>([]);
  const [polygonDetailsByPlaceId, setPolygonDetailsByPlaceId] = useState<Record<string, DeprioritisedPolygon>>({});
  const [loadingList, setLoadingList] = useState(true);
  const [loadingPage, setLoadingPage] = useState(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(DEFAULT_PAGINATION_MODEL);
  const [searchValue, setSearchValue] = useState('');

  const loadPolygons = useCallback(async () => {
    setLoadingList(true);

    const response = await SearchService.getTypeaheadDeprioritisations();
    setDeprioritisedPlaceIds(response.result.map(({ placeId }) => placeId));
    setPolygonDetailsByPlaceId({});
    setPaginationModel((currentPaginationModel) => ({
      ...currentPaginationModel,
      page: 0,
    }));
    setLoadingList(false);
  }, []);

  const filteredPlaceIds = useMemo(() => {
    const normalizedSearchValue = searchValue.trim().toLowerCase();

    if (!normalizedSearchValue) {
      return deprioritisedPlaceIds;
    }

    return deprioritisedPlaceIds.filter((placeId) => {
      if (placeId.toLowerCase().includes(normalizedSearchValue)) {
        return true;
      }

      const polygon = polygonDetailsByPlaceId[placeId];
      return polygon?.name?.toLowerCase().includes(normalizedSearchValue);
    });
  }, [deprioritisedPlaceIds, polygonDetailsByPlaceId, searchValue]);

  const currentPagePlaceIds = useMemo(() => {
    const start = paginationModel.page * paginationModel.pageSize;
    const end = start + paginationModel.pageSize;

    return filteredPlaceIds.slice(start, end);
  }, [filteredPlaceIds, paginationModel.page, paginationModel.pageSize]);

  const currentPageRows = useMemo(
    () => currentPagePlaceIds.map((placeId) => polygonDetailsByPlaceId[placeId]).filter(Boolean),
    [currentPagePlaceIds, polygonDetailsByPlaceId],
  );

  useEffect(() => {
    loadPolygons();
  }, [loadPolygons]);

  useEffect(() => {
    setPaginationModel((currentPaginationModel) => ({
      ...currentPaginationModel,
      page: 0,
    }));
  }, [searchValue]);

  useEffect(() => {
    const placeIdsToLoad = searchValue.trim() ? deprioritisedPlaceIds : currentPagePlaceIds;
    const missingPlaceIds = placeIdsToLoad.filter((placeId) => !polygonDetailsByPlaceId[placeId]);

    if (missingPlaceIds.length === 0) {
      setLoadingPage(false);
      return;
    }

    let isCancelled = false;

    async function loadCurrentPageDetails() {
      setLoadingPage(true);

      const loadedPolygons = await Promise.all(missingPlaceIds.map((placeId) => getDeprioritisedPolygon(placeId)));

      if (isCancelled) {
        return;
      }

      setPolygonDetailsByPlaceId((currentPolygonDetailsByPlaceId) => ({
        ...currentPolygonDetailsByPlaceId,
        ...Object.fromEntries(loadedPolygons.map((polygon) => [polygon.placeId, polygon])),
      }));
      setLoadingPage(false);
    }

    loadCurrentPageDetails();

    return () => {
      isCancelled = true;
    };
  }, [currentPagePlaceIds, deprioritisedPlaceIds, polygonDetailsByPlaceId, searchValue]);

  const handleRestore = useCallback(async (placeId: string) => {
    await SearchService.deleteTypeaheadDeprioritisation(placeId);
    setPolygonDetailsByPlaceId((currentPolygonDetailsByPlaceId) => {
      const nextPolygonDetailsByPlaceId = { ...currentPolygonDetailsByPlaceId };
      delete nextPolygonDetailsByPlaceId[placeId];
      return nextPolygonDetailsByPlaceId;
    });
    setDeprioritisedPlaceIds((currentPlaceIds) => {
      const nextPlaceIds = currentPlaceIds.filter((currentPlaceId) => currentPlaceId !== placeId);

      setPaginationModel((currentPaginationModel) => ({
        ...currentPaginationModel,
        page: Math.min(
          currentPaginationModel.page,
          Math.max(0, Math.ceil(nextPlaceIds.length / currentPaginationModel.pageSize) - 1),
        ),
      }));

      return nextPlaceIds;
    });
  }, []);

  return (
    <Stack spacing={2}>
      <Alert severity="info">
        This list loads all currently deprioritised polygons from the server so the whole team can review or restore
        them.
      </Alert>

      <Box>
        <Button onClick={loadPolygons} variant="contained" disabled={loadingList || loadingPage}>
          Refresh
        </Button>
      </Box>

      <TextField
        label="Search deprioritised polygons"
        placeholder="Filter by place ID or name"
        value={searchValue}
        onChange={(event) => setSearchValue(event.target.value)}
        fullWidth
      />

      <DataGrid
        loading={loadingList || loadingPage}
        columns={[
          { field: 'placeId', headerName: 'Place ID', flex: 1, sortable: false, display: 'flex' },
          { field: 'name', headerName: 'Name', flex: 1, sortable: false, display: 'flex' },
          { field: 'type', headerName: 'Type', flex: 1, sortable: false, display: 'flex' },
          {
            field: 'actions',
            headerName: 'Actions',
            width: 180,
            sortable: false,
            renderCell: (params) => (
              <Button variant="outlined" onClick={() => handleRestore(params.row.placeId)}>
                Restore priority
              </Button>
            ),
            display: 'flex',
          },
        ]}
        rows={currentPageRows}
        rowCount={filteredPlaceIds.length}
        getRowId={(row) => row.placeId}
        autoHeight
        pagination
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[100]}
        disableColumnFilter
        disableColumnMenu
        disableRowSelectionOnClick
        slots={{
          noRowsOverlay: () => (
            <Box sx={{ p: 3 }}>
              {searchValue.trim()
                ? 'No deprioritised polygons match your search.'
                : 'No deprioritised polygons found yet. Deprioritise a polygon from Search Dashboard and it will appear here.'}
            </Box>
          ),
        }}
      />
    </Stack>
  );
}
